import { useState, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Copy, Check, RefreshCw, ThumbsUp, ThumbsDown, Loader2, Bug } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import { FeedbackModal } from './feedback-modal'
import { api } from '@/api/client'
import { formatLocaleDateTime } from '@/lib/format-utils'

// Thin, form-local schema for the editable-reply textarea (not the raw
// contracts-package schema — this form's only input is the edited reply
// body). Mirrors the manual `if (!editedText.trim())` guard the Send button
// used to enforce. Deliberately a non-transforming refine, not
// `.trim().min(1)` — Zod's `.trim()` transforms the parsed value itself,
// which would silently strip intentional leading/trailing whitespace or
// line breaks from the message actually sent to the guest (Codex PR
// review); this only checks non-blankness, it never rewrites the text.
const editReplySchema = z.object({
  text: z.string().refine(value => value.trim().length > 0, 'A reply is required'),
})
type EditReplyValues = z.infer<typeof editReplySchema>

/**
 * Feedback recorded against a single suggestion version, either from the
 * backend or held optimistically until a refetch confirms it.
 */
interface SuggestionFeedback {
  rating?: 'up' | 'down' | null
  justification?: string | null
}

/**
 * Shape of a single AI reply suggestion - only the fields this component
 * actually reads. `metadata`/`error` are debug-only payloads shown verbatim
 * in the Debug Info dialog, so they're kept loose (the debug dump prints
 * arbitrary nested JSON) rather than fully modeled field-by-field.
 */
export interface Suggestion {
  id?: string
  message_id?: string
  version?: number
  status?: string
  text?: string
  instructions?: string
  sent_at?: string | null
  sent_by?: string | null
  feedback?: SuggestionFeedback | null
  imageUrls?: string[]
  model?: string
  tokens?: Record<string, unknown>
  created_at?: string
  createdAt?: string
  updated_at?: string
  updatedAt?: string
  error?: {
    code?: string
    message?: string
    details?: unknown
  } | null
  metadata?: {
    timing?: {
      queued_at?: string
      consumer_picked_up_at?: string
      llm_processing_start_at?: string
      llm_processing_end_at?: string
      completed_at?: string
    }
    cost_usd?: number
    llm_provider?: string
    provider?: string
    model?: string
    provider_settings?: unknown
    tokens?: Record<string, unknown>
    usage?: Record<string, unknown>
  } | null
}

interface SuggestedReplyCardProps {
  messageId: string
  suggestions?: Suggestion[]
  instructions?: string
  onRegenerate: (messageId: string, mode: string) => void
  onAddInstructions: (messageId: string) => void
  isStreaming?: boolean
  onFeedbackUpdate?: () => void
}

/**
 * SuggestedReplyCard component
 */
export function SuggestedReplyCard({
  messageId,
  suggestions,
  instructions,
  onRegenerate,
  onAddInstructions,
  isStreaming = false,
  onFeedbackUpdate, // Optional callback to refresh suggestions after feedback
}: SuggestedReplyCardProps) {
  const { toast } = useToast()
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [debugModalOpen, setDebugModalOpen] = useState(false)
  const [selectedDebugSuggestionVersion, setSelectedDebugSuggestionVersion] = useState<
    number | null
  >(null)
  const [savingFeedback, setSavingFeedback] = useState(false)
  const [pendingFeedbackVersion, setPendingFeedbackVersion] = useState<number | null>(null)
  const [optimisticFeedback, setOptimisticFeedback] = useState<Record<number, SuggestionFeedback>>(
    {}
  ) // { version: { rating, justification } }
  const [isEditing, setIsEditing] = useState(false)
  const form = useForm<EditReplyValues>({
    resolver: zodResolver(editReplySchema),
    defaultValues: { text: '' },
  })
  const editedTextValue = useWatch({ control: form.control, name: 'text' })
  const [confirmSendOpen, setConfirmSendOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [pendingIdempotencyKey, setPendingIdempotencyKey] = useState<string | null>(null)
  // The body text pendingIdempotencyKey was generated for — lets
  // onSubmitEdit tell "same logical send, reuse the key" apart
  // from "text changed, this is a new one" across dialog dismiss/reopen.
  const [pendingSendBody, setPendingSendBody] = useState<string | null>(null)
  // Version pinned at the moment editing started (Codex PR review): the
  // Regenerate control is disabled while editing (below), but an already
  // in-flight generation started BEFORE editing began could still complete
  // mid-edit and advance latestSuggestion's version out from under the
  // operator's draft. Sending must target the version the operator was
  // actually looking at, not whatever's newest by the time they click Send.
  const [editingVersion, setEditingVersion] = useState<number | null>(null)
  // Set once a send genuinely succeeds, before the parent's refetch (async,
  // not awaited) has necessarily updated `suggestions` with a real sent_at
  // (Codex PR review): without this, the card would briefly render as
  // "Ready" and clickable again using stale props, letting the operator
  // start and submit a second edit — a real duplicate-send window — before
  // the authoritative backend state arrives. Not optimistic display of
  // unconfirmed data (admin-ui CLAUDE.md) — this only gates re-entering
  // edit mode, using a fact the backend's own response already confirmed;
  // no fabricated timestamp or sent-by is ever shown from it.
  // Which version was actually sent, not a bare boolean (Codex PR review,
  // a further pass): if a background regeneration (already in flight
  // before editing began) publishes a NEWER version while this send is
  // landing, a scope-less flag would mark that newer, never-sent
  // suggestion as permanently "Sent" too — and since nothing ever cleared
  // it, that stuck even after a real refetch arrived with accurate data.
  const [justSentVersion, setJustSentVersion] = useState<number | null>(null)

  // Get latest suggestion (last in array, sorted by version)
  const latestSuggestion = useMemo(() => {
    if (!suggestions || suggestions.length === 0) return null
    // Sort by version (descending) and take the first
    const sorted = [...suggestions].sort((a, b) => (b.version || 0) - (a.version || 0))
    return sorted[0]
  }, [suggestions])
  const hasInstructions = Boolean(instructions && instructions.trim().length > 0)
  const hasHistory = Boolean(suggestions && suggestions.length > 1)
  const hasSuggestion = Boolean(
    latestSuggestion && latestSuggestion.text && latestSuggestion.text.trim().length > 0
  )
  const isSent =
    Boolean(latestSuggestion?.sent_at) ||
    (justSentVersion !== null && latestSuggestion?.version === justSentVersion)

  // Determine display status (UI can show "thinking" but actual status is "queued")
  const status = useMemo(() => {
    if (isStreaming) return 'generating'
    if (!latestSuggestion) return 'not-generated'
    const statusLower = (latestSuggestion.status || '').toLowerCase()
    if (statusLower === 'completed') return 'completed'
    if (statusLower === 'failed') return 'failed'
    if (statusLower === 'accepted') return 'accepted'
    if (statusLower === 'queued') return 'generating' // Show as "thinking/generating" if queued
    if (hasSuggestion) return 'completed' // If we have text, treat as completed
    // If suggestion exists but no text yet, show as generating (might be in progress)
    if (latestSuggestion) return 'generating'
    return 'not-generated'
  }, [latestSuggestion, hasSuggestion, isStreaming])

  const handleCopy = async () => {
    if (!hasSuggestion || !latestSuggestion?.text) return

    try {
      await navigator.clipboard.writeText(latestSuggestion.text)
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 3000)
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy suggested reply to clipboard',
        variant: 'destructive',
      })
    }
  }

  // Gmail-style: clicking the static suggestion text turns it into an
  // editable textarea, pre-filled with the suggestion's own text. Only
  // reachable for a ready, not-yet-sent suggestion.
  const handleStartEdit = () => {
    if (!hasSuggestion || status !== 'completed' || isSent || !latestSuggestion) return
    form.reset({ text: latestSuggestion.text ?? '' })
    setEditingVersion(latestSuggestion.version ?? null)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    form.reset({ text: '' })
    setEditingVersion(null)
    // pendingIdempotencyKey/pendingSendBody deliberately NOT cleared here
    // (Codex PR review, a further pass on the idempotency-key-reuse fix):
    // this only closes the confirm dialog/edit view, not the underlying
    // pending send attempt. If the operator dismisses an ambiguous failure
    // (Cancel/Escape) and reopens Send with the SAME text, the key must
    // still be reused — see onSubmitEdit.
  }

  // Reused across the send's whole lifecycle for a given body text, not
  // just "while the dialog stays open" (Codex PR review, two passes): the
  // first version of this only survived retries within one still-open
  // dialog session, but the confirm dialog CAN be dismissed after an
  // ambiguous failure resolves (sending becomes false again) — Cancel or
  // Escape. If the operator then reopened Send with the SAME unedited
  // text, this used to generate a brand-new key, letting the backend treat
  // it as an unrelated send even though the prior ambiguous attempt might
  // already have reached Uplisting. Now the key is only regenerated when
  // the body text actually differs from the one it was last issued for —
  // a genuinely different logical send. Same key + same body, no matter
  // how many times the dialog is opened/closed/retried, always reuses.
  const onSubmitEdit = (values: EditReplyValues) => {
    if (!pendingIdempotencyKey || pendingSendBody !== values.text) {
      setPendingIdempotencyKey(crypto.randomUUID())
      setPendingSendBody(values.text)
    }
    setConfirmSendOpen(true)
  }

  // form.handleSubmit(onSubmitEdit) reads internal refs, so it's wrapped
  // here and invoked from this click handler rather than called inline
  // during render (same pattern as composer.tsx/ask-tab.tsx) — there's no
  // <form> element here (matching the original plain-div edit chrome), so
  // this is the only place the actual submission is triggered from.
  const handleSendClick = () => {
    void form.handleSubmit(onSubmitEdit)()
  }

  // Send is never automatic — this only runs from the explicit confirm
  // dialog below (root CLAUDE.md: "Reply suggestions: operators always
  // review before sending — system never auto-sends"). The button
  // disables immediately (via `sending`) so a double-click can't fire two
  // requests, and repeated calls (a retry after an ambiguous failure)
  // reuse the same pendingIdempotencyKey — see onSubmitEdit.
  const handleConfirmSend = async () => {
    if (
      !latestSuggestion ||
      sending ||
      !pendingIdempotencyKey ||
      pendingSendBody === null ||
      editingVersion === null
    )
      return
    setSending(true)
    try {
      // editingVersion (pinned at handleStartEdit), not
      // latestSuggestion.version — the underlying suggestion could have
      // advanced to a newer version while this edit was in flight (Codex
      // PR review); sending must target the version the operator was
      // actually looking at.
      await api.sendSuggestionMessage(messageId, editingVersion, {
        body: pendingSendBody,
        idempotencyKey: pendingIdempotencyKey,
      })
      toast({
        title: 'Message sent',
        description: 'The guest has been sent this reply.',
      })
      setConfirmSendOpen(false)
      setIsEditing(false)
      form.reset({ text: '' })
      setPendingIdempotencyKey(null)
      setPendingSendBody(null)
      // A real send just succeeded — lock THIS version from being
      // re-entered even before the refetch below lands (Codex PR review:
      // without this, stale props briefly render as "Ready" and clickable
      // again, letting the operator start and submit a second edit before
      // the authoritative sent_at arrives). Scoped to editingVersion
      // specifically (not a bare boolean — Codex PR review, a further
      // pass) so a newer, never-sent suggestion that appears in the
      // meantime is never mistakenly locked as sent too. Captured before
      // clearing editingVersion below.
      setJustSentVersion(editingVersion)
      setEditingVersion(null)
      // No optimistic DISPLAY of unconfirmed data — refetch so the Sent
      // badge's timestamp/actor come from the backend's own record.
      onFeedbackUpdate?.()
    } catch (error: unknown) {
      const err = error as { body?: { error?: string }; message?: string }
      const reason = err?.body?.error || err?.message || 'Please try again'
      toast({
        title: 'Failed to send message',
        description: reason,
        variant: 'destructive',
      })
      // Stay open so the operator can see the error and decide whether to
      // retry, adjust the text, or cancel — never silently drop back to
      // the static view on a failure.
    } finally {
      setSending(false)
    }
  }

  const getStatusBadge = () => {
    if (isSent) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="success" className="text-xs px-1.5 py-0 h-4">
              Sent
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              Sent {formatLocaleDateTime(latestSuggestion?.sent_at, { fallback: '' })}
              {latestSuggestion?.sent_by ? ` by ${latestSuggestion.sent_by}` : ''}
            </p>
          </TooltipContent>
        </Tooltip>
      )
    }
    if (status === 'generating') {
      return (
        <Badge variant="default" className="text-xs px-1.5 py-0 h-4 animate-pulse">
          Thinking…
        </Badge>
      )
    }
    if (status === 'completed') {
      return (
        <Badge variant="success" className="text-xs px-1.5 py-0 h-4">
          Ready
        </Badge>
      )
    }
    if (status === 'failed') {
      const errorMessage = latestSuggestion?.error?.message || 'Unknown error'
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="destructive" className="text-xs px-1.5 py-0 h-4 opacity-100">
              Failed
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-md">
            <p className="wrap-break-word">{errorMessage}</p>
          </TooltipContent>
        </Tooltip>
      )
    }
    if (status === 'accepted') {
      return (
        <Badge variant="success" className="text-xs px-1.5 py-0 h-4">
          Accepted
        </Badge>
      )
    }
    return (
      <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
        Not generated
      </Badge>
    )
  }

  // Combined handler: if instructions exist, regenerate; otherwise, open instructions modal
  const handleInstructionsOrRegenerate = () => {
    if (hasInstructions) {
      // If instructions exist, regenerate with them
      onRegenerate(messageId, 'regenerate')
    } else {
      // If no instructions, open modal to add them
      onAddInstructions(messageId)
    }
  }

  // Get feedback for a suggestion (from actual data or optimistic state)
  const getFeedback = (suggestion: Suggestion): SuggestionFeedback | null => {
    const version = suggestion.version
    if (version !== undefined && optimisticFeedback[version]) {
      return optimisticFeedback[version]
    }
    return suggestion.feedback || null
  }

  // Handle thumbs up click
  const handleThumbsUp = async (suggestion: Suggestion) => {
    const version = suggestion.version
    if (version === undefined) return
    const currentFeedback = getFeedback(suggestion)
    const newRating = currentFeedback?.rating === 'up' ? null : 'up'

    // Optimistic update
    setOptimisticFeedback(prev => ({
      ...prev,
      [version]: { rating: newRating, justification: null },
    }))
    setSavingFeedback(true)

    try {
      await api.submitSuggestionFeedback(messageId, version, {
        rating: newRating,
        justification: null,
      })

      toast({
        title: newRating ? 'Feedback saved' : 'Feedback cleared',
        description: newRating ? 'Thank you for your feedback!' : '',
      })

      // Refresh suggestions if callback provided
      if (onFeedbackUpdate) {
        onFeedbackUpdate()
      } else {
        // Clear optimistic state after a delay to allow parent to refresh
        setTimeout(() => {
          setOptimisticFeedback(prev => {
            const next = { ...prev }
            delete next[version]
            return next
          })
        }, 1000)
      }
    } catch (error: unknown) {
      // Revert optimistic update
      setOptimisticFeedback(prev => {
        const next = { ...prev }
        delete next[version]
        return next
      })

      toast({
        title: 'Failed to save feedback',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setSavingFeedback(false)
    }
  }

  // Handle thumbs down click
  const handleThumbsDown = (suggestion: Suggestion) => {
    if (suggestion.version === undefined) return
    setPendingFeedbackVersion(suggestion.version)
    setFeedbackModalOpen(true)
  }

  // Handle feedback modal save
  const handleFeedbackSave = async (justification: string | null) => {
    if (pendingFeedbackVersion === null) return

    const suggestion = suggestions?.find(s => s.version === pendingFeedbackVersion)
    if (!suggestion) return

    const currentFeedback = getFeedback(suggestion)
    const newRating = currentFeedback?.rating === 'down' ? null : 'down'

    // Optimistic update
    setOptimisticFeedback(prev => ({
      ...prev,
      [pendingFeedbackVersion]: { rating: newRating, justification },
    }))
    setSavingFeedback(true)
    setFeedbackModalOpen(false)

    try {
      await api.submitSuggestionFeedback(messageId, pendingFeedbackVersion, {
        rating: newRating,
        justification,
      })

      toast({
        title: 'Feedback saved',
        description: 'Thank you for helping us improve!',
      })

      // Refresh suggestions if callback provided
      if (onFeedbackUpdate) {
        onFeedbackUpdate()
      } else {
        // Clear optimistic state after a delay
        setTimeout(() => {
          setOptimisticFeedback(prev => {
            const next = { ...prev }
            delete next[pendingFeedbackVersion]
            return next
          })
        }, 1000)
      }
    } catch (error: unknown) {
      // Revert optimistic update
      setOptimisticFeedback(prev => {
        const next = { ...prev }
        delete next[pendingFeedbackVersion]
        return next
      })

      toast({
        title: 'Failed to save feedback',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setSavingFeedback(false)
      setPendingFeedbackVersion(null)
    }
  }

  // Render feedback icons for a suggestion
  const renderFeedbackIcons = (suggestion: Suggestion | null) => {
    if (!suggestion || status !== 'completed') return null

    const feedback = getFeedback(suggestion)
    const rating = feedback?.rating
    const version = suggestion.version
    const isSaving = savingFeedback && pendingFeedbackVersion === version

    return (
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleThumbsUp(suggestion)}
              disabled={isSaving}
              className={`transition-colors ${
                rating === 'up'
                  ? 'text-mint opacity-100'
                  : 'text-textMuted opacity-60 hover:opacity-100'
              } disabled:opacity-50`}
            >
              {isSaving && rating === 'up' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ThumbsUp className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Helpful</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleThumbsDown(suggestion)}
              disabled={isSaving}
              className={`transition-colors ${
                rating === 'down'
                  ? 'text-mint opacity-100'
                  : 'text-textMuted opacity-60 hover:opacity-100'
              } disabled:opacity-50`}
            >
              {isSaving && rating === 'down' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ThumbsDown className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Not helpful</p>
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="mt-3">
      <div className="flex justify-start items-start gap-2">
        {/* Action buttons - vertical stack */}
        <div className="flex flex-col gap-5 mt-1.5">
          {hasSuggestion && status === 'completed' && (
            <Tooltip open={copiedMessageId === messageId}>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCopy}
                  className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                >
                  {copiedMessageId === messageId ? (
                    <Check className="h-4 w-4 text-mint" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copied!</p>
              </TooltipContent>
            </Tooltip>
          )}
          <button
            onClick={handleInstructionsOrRegenerate}
            // Disabled while editing (Codex PR review): regenerating mid-edit
            // would advance the underlying suggestion's version out from
            // under the operator's in-progress draft.
            disabled={isStreaming || status === 'generating' || isEditing}
            className="text-textMuted hover:text-text transition-colors disabled:opacity-50 opacity-60 hover:opacity-100"
            title={hasInstructions ? 'Regenerate with instructions' : 'Add instructions'}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  const latestVersion = latestSuggestion?.version ?? null
                  setSelectedDebugSuggestionVersion(latestVersion)
                  setDebugModalOpen(true)
                }}
                className="text-textMuted hover:text-text transition-colors opacity-60 hover:opacity-100"
                title="Debug info"
              >
                <Bug className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Debug info</p>
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Suggestion bubble */}
        {hasSuggestion && latestSuggestion ? (
          <div className="group rounded-lg px-4 py-2.5 max-w-[70%] bg-mint/10 border border-mint/40">
            {/* Header: Label and status badge inside bubble */}
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Suggested reply</span>
                {getStatusBadge()}
              </div>
              {renderFeedbackIcons(latestSuggestion)}
            </div>
            {hasInstructions && latestSuggestion.instructions && (
              <div className="text-xs text-textMuted italic mb-1.5">Used instructions</div>
            )}
            {isEditing ? (
              <Form {...form}>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="text"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <textarea
                            {...field}
                            autoFocus
                            rows={4}
                            aria-label="Edit suggested reply before sending"
                            className="w-full text-sm leading-relaxed px-2 py-1.5 border border-mintBorder rounded-md bg-surface text-text focus:outline-hidden focus:ring-2 focus:ring-mint"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={handleSendClick} disabled={!editedTextValue?.trim()}>
                      Send
                    </Button>
                    <Button size="sm" variant="secondary" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </Form>
            ) : status === 'completed' && !isSent ? (
              // A real <button> (Codex PR review: a plain onClick div has
              // no keyboard affordance at all — not focusable, no role,
              // Enter/Space do nothing) rather than reimplementing
              // focus/keydown handling by hand.
              <button
                type="button"
                onClick={handleStartEdit}
                className="w-full text-left text-sm whitespace-pre-wrap leading-relaxed cursor-text hover:bg-mint/5 rounded -m-1 p-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint"
                title="Click to edit before sending"
              >
                {latestSuggestion.text}
              </button>
            ) : (
              <div className="text-sm whitespace-pre-wrap leading-relaxed">
                {latestSuggestion.text}
              </div>
            )}
            {/* Render images if present */}
            {latestSuggestion.imageUrls && latestSuggestion.imageUrls.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {latestSuggestion.imageUrls.map((imageUrl, index) => {
                  // Extract object key from URL if it's a full URL
                  let imageSrc = imageUrl
                  if (imageUrl.startsWith('http')) {
                    // If it's already a full URL, use it directly
                    imageSrc = imageUrl
                  } else if (imageUrl.startsWith('instructions/')) {
                    // If it's an object key, construct the URL
                    const apiBase = import.meta.env.VITE_API_BASE_URL || ''
                    imageSrc = `${apiBase}/admin/images/${imageUrl}`
                  }

                  return (
                    <img
                      key={index}
                      src={imageSrc}
                      alt={`Suggestion image ${index + 1}`}
                      className="max-w-[200px] max-h-[200px] rounded-md object-cover border border-mint/20"
                      loading="lazy"
                      onError={e => {
                        // Hide broken images
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  )
                })}
              </div>
            )}
            {/* Show justification if present */}
            {getFeedback(latestSuggestion)?.justification && (
              <div className="mt-2 pt-2 border-t border-mint/20">
                <div className="text-xs text-textMuted italic">
                  Feedback: {getFeedback(latestSuggestion)?.justification}
                </div>
              </div>
            )}
            {/* Version badge in bottom right corner */}
            {status === 'completed' && hasHistory && (
              <div className="mt-2 flex justify-end">
                <span className="text-xs text-textMuted">v{latestSuggestion.version}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg px-4 py-2.5 max-w-[70%] bg-mint/5 border border-mint/20 opacity-60">
            {/* Header: Label and status badge inside bubble */}
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Suggested reply</span>
                {getStatusBadge()}
              </div>
            </div>
            <div className="text-sm text-textMuted italic">
              {isStreaming ? 'Thinking…' : 'Suggestion will appear here.'}
            </div>
          </div>
        )}
      </div>
      <FeedbackModal
        open={feedbackModalOpen}
        onOpenChange={setFeedbackModalOpen}
        onSave={handleFeedbackSave}
        isLoading={savingFeedback}
      />
      {/* Confirm send — the only place the actual send API call happens */}
      <Dialog
        open={confirmSendOpen}
        onOpenChange={open => {
          if (!sending) setConfirmSendOpen(open)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send this reply?</DialogTitle>
            <DialogDescription>
              This sends a real message to the guest via Uplisting. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm whitespace-pre-wrap leading-relaxed rounded-md border border-border bg-surface2 p-3">
            {pendingSendBody}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmSendOpen(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Debug Modal */}
      <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Debug Info</DialogTitle>
            <DialogDescription>
              AI model, parameters, tokens, errors, and generation time
            </DialogDescription>
          </DialogHeader>
          {suggestions && suggestions.length > 0 && (
            <div className="space-y-4">
              {/* Suggestion selector if multiple suggestions */}
              {suggestions.length > 1 && (
                <div className="flex gap-2 border-b border-border">
                  {suggestions
                    .slice()
                    .sort((a, b) => (b.version || 0) - (a.version || 0))
                    .map(s => (
                      <button
                        key={s.id}
                        onClick={() => setSelectedDebugSuggestionVersion(s.version ?? null)}
                        className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
                          selectedDebugSuggestionVersion === s.version
                            ? 'border-mint text-text'
                            : 'border-transparent text-textMuted hover:text-text'
                        }`}
                      >
                        v{s.version || '?'} {s.status === 'failed' && '❌'}
                        {s.status === 'completed' && '✅'}
                      </button>
                    ))}
                </div>
              )}
              {/* Debug info for selected suggestion */}
              {(() => {
                const selectedSuggestion =
                  suggestions.find(s => s.version === selectedDebugSuggestionVersion) ||
                  suggestions.slice().sort((a, b) => (b.version || 0) - (a.version || 0))[0] ||
                  null

                if (!selectedSuggestion) return null

                const formatDate = (dateStr?: string | null) => {
                  if (!dateStr) return 'N/A'
                  return formatLocaleDateTime(dateStr, { fallback: 'N/A' })
                }

                const calculateDuration = (startTime?: string | null, endTime?: string | null) => {
                  if (!startTime || !endTime) return 'N/A'
                  try {
                    const start = new Date(startTime)
                    const end = new Date(endTime)
                    const ms = end.getTime() - start.getTime()
                    if (ms < 1000) return `${ms}ms`
                    return `${(ms / 1000).toFixed(2)}s`
                  } catch {
                    return 'N/A'
                  }
                }

                const timing = selectedSuggestion.metadata?.timing || {}
                const timings = {
                  queued: timing.queued_at,
                  consumerPickedUp: timing.consumer_picked_up_at,
                  llmProcessingStart: timing.llm_processing_start_at,
                  llmProcessingEnd: timing.llm_processing_end_at,
                  completed:
                    timing.completed_at ||
                    selectedSuggestion.updated_at ||
                    selectedSuggestion.updatedAt,
                }
                const createdAt = selectedSuggestion.created_at || selectedSuggestion.createdAt

                return (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-textMuted mb-1">Status</div>
                        <div className="font-mono">{selectedSuggestion.status || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-textMuted mb-1">Version</div>
                        <div className="font-mono">{selectedSuggestion.version || 'N/A'}</div>
                      </div>
                      {selectedSuggestion.metadata?.cost_usd !== undefined && (
                        <div>
                          <div className="text-xs text-textMuted mb-1">Cost</div>
                          <div className="font-mono">
                            ${selectedSuggestion.metadata.cost_usd.toFixed(6)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Timing breakdown */}
                    <div>
                      <div className="text-xs text-textMuted mb-2">Timing Breakdown</div>
                      <div className="bg-surface2 p-3 rounded border border-border space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="text-textMuted">Queued:</span>{' '}
                            <span className="font-mono">
                              {timings.queued ? formatDate(timings.queued) : 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-textMuted">Consumer Picked Up:</span>{' '}
                            <span className="font-mono">
                              {timings.consumerPickedUp
                                ? formatDate(timings.consumerPickedUp)
                                : 'N/A'}
                            </span>
                            {timings.queued && timings.consumerPickedUp && (
                              <span className="text-textMuted ml-1">
                                ({calculateDuration(timings.queued, timings.consumerPickedUp)})
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-textMuted">LLM Processing Start:</span>{' '}
                            <span className="font-mono">
                              {timings.llmProcessingStart
                                ? formatDate(timings.llmProcessingStart)
                                : 'N/A'}
                            </span>
                            {timings.consumerPickedUp && timings.llmProcessingStart && (
                              <span className="text-textMuted ml-1">
                                (
                                {calculateDuration(
                                  timings.consumerPickedUp,
                                  timings.llmProcessingStart
                                )}
                                )
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-textMuted">LLM Processing End:</span>{' '}
                            <span className="font-mono">
                              {timings.llmProcessingEnd
                                ? formatDate(timings.llmProcessingEnd)
                                : 'N/A'}
                            </span>
                            {timings.llmProcessingStart && timings.llmProcessingEnd && (
                              <span className="text-textMuted ml-1">
                                (
                                {calculateDuration(
                                  timings.llmProcessingStart,
                                  timings.llmProcessingEnd
                                )}
                                )
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-textMuted">Completed:</span>{' '}
                            <span className="font-mono">
                              {timings.completed ? formatDate(timings.completed) : 'N/A'}
                            </span>
                            {timings.llmProcessingEnd && timings.completed && (
                              <span className="text-textMuted ml-1">
                                ({calculateDuration(timings.llmProcessingEnd, timings.completed)})
                              </span>
                            )}
                          </div>
                          <div>
                            <span className="text-textMuted font-semibold">Total Duration:</span>{' '}
                            <span className="font-mono font-semibold">
                              {calculateDuration(
                                timings.queued || createdAt,
                                timings.completed ||
                                  selectedSuggestion.updated_at ||
                                  selectedSuggestion.updatedAt
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Model info - Always show if metadata exists */}
                    {(selectedSuggestion.metadata?.llm_provider ||
                      selectedSuggestion.metadata?.provider ||
                      selectedSuggestion.metadata?.model ||
                      selectedSuggestion.model) && (
                      <div>
                        <div className="text-xs text-textMuted mb-1">AI Provider & Model</div>
                        <div className="font-mono bg-surface2 p-2 rounded border border-border text-xs">
                          <div>
                            <span className="text-textMuted">Provider:</span>{' '}
                            {selectedSuggestion.metadata?.llm_provider ||
                              selectedSuggestion.metadata?.provider ||
                              'N/A'}
                          </div>
                          <div>
                            <span className="text-textMuted">Model:</span>{' '}
                            {selectedSuggestion.metadata?.model ||
                              selectedSuggestion.model ||
                              'N/A'}
                          </div>
                          {selectedSuggestion.metadata?.provider_settings ? (
                            <div className="mt-1 pt-1 border-t border-border">
                              <span className="text-textMuted">Parameters:</span>
                              <pre className="whitespace-pre-wrap text-xs mt-1">
                                {JSON.stringify(
                                  selectedSuggestion.metadata.provider_settings,
                                  null,
                                  2
                                )}
                              </pre>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {/* Tokens */}
                    {(selectedSuggestion.metadata?.tokens ||
                      selectedSuggestion.metadata?.usage ||
                      selectedSuggestion.tokens) && (
                      <div>
                        <div className="text-xs text-textMuted mb-1">Tokens</div>
                        <div className="font-mono bg-surface2 p-2 rounded border border-border">
                          <pre className="whitespace-pre-wrap text-xs">
                            {JSON.stringify(
                              selectedSuggestion.metadata?.tokens ||
                                selectedSuggestion.metadata?.usage ||
                                selectedSuggestion.tokens ||
                                {},
                              null,
                              2
                            )}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Error */}
                    {selectedSuggestion.error && (
                      <div>
                        <div className="text-xs text-textMuted mb-1">Error</div>
                        <div className="font-mono bg-destructive/10 p-2 rounded border border-destructive/20 text-xs text-destructive wrap-break-word">
                          <div className="font-semibold mb-1">
                            {selectedSuggestion.error.code || 'Error'}
                          </div>
                          <div>{selectedSuggestion.error.message || 'Unknown error'}</div>
                          {selectedSuggestion.error.details ? (
                            <div className="mt-2 pt-2 border-t border-destructive/20">
                              <pre className="whitespace-pre-wrap text-xs">
                                {JSON.stringify(selectedSuggestion.error.details, null, 2)}
                              </pre>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {/* Instructions */}
                    {selectedSuggestion.instructions && (
                      <div>
                        <div className="text-xs text-textMuted mb-1">Instructions</div>
                        <div className="font-mono bg-surface2 p-2 rounded border border-border text-xs">
                          {selectedSuggestion.instructions}
                        </div>
                      </div>
                    )}

                    {/* Full metadata dump */}
                    {selectedSuggestion.metadata && (
                      <details className="mt-4">
                        <summary className="text-xs text-textMuted cursor-pointer hover:text-text mb-2">
                          Full Metadata
                        </summary>
                        <div className="font-mono bg-surface2 p-2 rounded border border-border text-xs overflow-x-auto">
                          <pre className="whitespace-pre-wrap">
                            {JSON.stringify(selectedSuggestion.metadata, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}

                    {/* Full suggestion object */}
                    <details className="mt-4">
                      <summary className="text-xs text-textMuted cursor-pointer hover:text-text mb-2">
                        Full Suggestion Object
                      </summary>
                      <div className="font-mono bg-surface2 p-2 rounded border border-border text-xs overflow-x-auto max-h-64 overflow-y-auto">
                        <pre className="whitespace-pre-wrap">
                          {JSON.stringify(selectedSuggestion, null, 2)}
                        </pre>
                      </div>
                    </details>
                  </div>
                )
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
